import React from "react";
import {Typography} from "@mui/material";

import {asObligation} from "./SlotState";
import {Grey6, Grey17} from "../../theme/styles";

// An approval request, drawn.
//
// WHY A PICTURE AND NOT A BETTER LIST. An approval IS a graph -- stages in sequence, slots within a
// stage, a quorum group as a cluster, and a state per node. The flat list could say who had signed
// but not that stage 1 GATES stage 2, so "why can nobody approve this yet?" had no visible answer.
// That is the question people actually arrive with.
//
// STATIC SVG, NOT A DIAGRAMMING LIBRARY. Nothing here is interactive: no dragging, no editing, no
// hit-testing. jointjs earns its weight in the workflow editor because that editor is a canvas
// somebody builds on; this is a read-only picture whose layout is decided entirely by the data. SVG
// costs no dependency, prints, scales, and renders in a PDF export. If this ever becomes editable,
// that is the moment to reach for the library -- not before.
//
// It is also why the layout is computed rather than laid out by an engine: stages are columns in
// order, slots are rows within a column, and that is the whole algorithm.

type Row = Record<string, any>;

const NODE_W = 230;
const NODE_H = 56;
const NODE_GAP = 14;
const COL_GAP = 58;
const TOP = 34;

const STATE = {
    signed:   {fill: "#ECFDF3", stroke: "#067647", ink: "#065F46"},
    rejected: {fill: "#FEE4E2", stroke: "#B42318", ink: "#912018"},
    open:     {fill: "#FFFAEB", stroke: "#B54708", ink: "#8A5A00"},
    waiting:  {fill: "#F9FAFB", stroke: "#D0D5DD", ink: "#667085"},
};

/** SVG does not wrap text, so anything long is cut and the full value goes in a <title>. */
function clip(value: string | null | undefined, max: number) {
    const text = (value ?? "").trim();
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function slotState(slot: Row, currentStage: number | null) {
    if (slot.Signature) {
        return String(slot.Signature.Decision ?? "").toUpperCase().startsWith("REJECT")
            ? "rejected" : "signed";
    }
    if (typeof slot.Stage === "number" && typeof currentStage === "number" && slot.Stage > currentStage) {
        return "waiting";
    }
    return "open";
}

export default function ApprovalDiagram({request}: {request: Row | null | undefined}) {
    const slots: Row[] = request?.Slots ?? [];
    if (!request || slots.length === 0) return null;

    const currentStage = typeof request.CurrentStage === "number" ? request.CurrentStage : null;

    // Stages in order, each holding its slots in reading order.
    const stageNumbers = Array.from(new Set(slots.map(one => one.Stage ?? 1)))
        .sort((a: number, b: number) => a - b);
    const columns = stageNumbers.map(stage => ({
        stage,
        label: slots.find(one => (one.Stage ?? 1) === stage)?.StageLabel ?? null,
        slots: slots.filter(one => (one.Stage ?? 1) === stage),
    }));

    const tallest = Math.max(...columns.map(column => column.slots.length));
    const width = columns.length * NODE_W + (columns.length - 1) * COL_GAP;
    const height = TOP + tallest * NODE_H + (tallest - 1) * NODE_GAP + 10;

    // How many slots share each quorum group, so a node can say "any 1 of 3" rather than naming a
    // group nobody outside the matrix editor has heard of.
    const quorumSize: Record<string, number> = {};
    slots.forEach(one => {
        if (one.QuorumGroup) quorumSize[one.QuorumGroup] = (quorumSize[one.QuorumGroup] ?? 0) + 1;
    });

    return (
        <div style={{margin: "4px 0 14px"}}>
            <div style={{overflowX: "auto"}}>
                <svg viewBox={`0 0 ${width} ${height}`} width="100%"
                     style={{maxWidth: width, height: "auto", display: "block"}}
                     role="img"
                     aria-label={`Approval progress: stage ${currentStage ?? 1} of ${columns.length}`}>
                    <defs>
                        <marker id="approvalArrow" viewBox="0 0 10 10" refX="9" refY="5"
                                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                            <path d="M 0 0 L 10 5 L 0 10 z" fill="#98A2B3"/>
                        </marker>
                    </defs>

                    {columns.map((column, index) => {
                        const x = index * (NODE_W + COL_GAP);
                        const done = currentStage !== null && column.stage < currentStage;
                        return (
                            <g key={column.stage}>
                                <text x={x} y={14} fontSize={11} fontWeight={700} fill={Grey6}
                                      letterSpacing="0.06em">
                                    {`STAGE ${column.stage}${column.label ? ` · ${column.label.toUpperCase()}` : ""}`}
                                </text>
                                {done ? (
                                    <text x={x + NODE_W} y={14} fontSize={11} fill="#067647"
                                          textAnchor="end">complete</text>
                                ) : currentStage === column.stage ? (
                                    <text x={x + NODE_W} y={14} fontSize={11} fill="#B54708"
                                          textAnchor="end">open now</text>
                                ) : null}

                                {column.slots.map((slot, row) => {
                                    const y = TOP + row * (NODE_H + NODE_GAP);
                                    const state = slotState(slot, currentStage);
                                    const palette = STATE[state as keyof typeof STATE];
                                    const signature = slot.Signature;
                                    const quorum = slot.QuorumGroup
                                        ? `any ${slot.QuorumRequired ?? 1} of ${quorumSize[slot.QuorumGroup]}`
                                        : null;

                                    return (
                                        <g key={slot.USID ?? `${column.stage}-${row}`}>
                                            <title>
                                                {`${slot.PersonaName ?? ""} — `
                                                    + (signature
                                                        ? `${state === "rejected" ? "rejected" : String(slot.Meaning ?? "signed").toLowerCase()}`
                                                          + `${signature.PrintedName ? ` by ${signature.PrintedName}` : ""}`
                                                        : asObligation(slot.Meaning ?? null))}
                                            </title>
                                            <rect x={x} y={y} width={NODE_W} height={NODE_H} rx={10}
                                                  fill={palette.fill} stroke={palette.stroke}
                                                  strokeWidth={state === "open" ? 2 : 1}
                                                  strokeDasharray={state === "waiting" ? "5 4" : undefined}/>

                                            <text x={x + 12} y={y + 21} fontSize={12.5} fontWeight={600}
                                                  fill={Grey17}>
                                                {clip(slot.PersonaName, 26)}
                                            </text>

                                            <text x={x + 12} y={y + 38} fontSize={11.5} fill={palette.ink}>
                                                {signature
                                                    ? clip((state === "rejected"
                                                        ? "rejected"
                                                        : String(slot.Meaning ?? "signed").toLowerCase())
                                                        + (signature.PrintedName ? ` by ${signature.PrintedName}` : ""), 30)
                                                    : asObligation(slot.Meaning ?? null)}
                                            </text>

                                            {quorum ? (
                                                <text x={x + 12} y={y + 51} fontSize={10} fill={Grey6}>
                                                    {quorum}
                                                </text>
                                            ) : null}

                                            {/* The mark, for anyone who cannot separate the colours. */}
                                            <text x={x + NODE_W - 12} y={y + 24} fontSize={14}
                                                  textAnchor="end" fill={palette.stroke}>
                                                {state === "signed" ? "✓" : state === "rejected" ? "✕" : "○"}
                                            </text>
                                        </g>
                                    );
                                })}

                                {/* ARROWS CARRY THE ONLY THING THE LIST COULD NOT SAY: that this stage
                                    gates the next one. Drawn from the middle of each column's block. */}
                                {index < columns.length - 1 ? (
                                    <line x1={x + NODE_W + 8} y1={TOP + NODE_H / 2}
                                          x2={x + NODE_W + COL_GAP - 8} y2={TOP + NODE_H / 2}
                                          stroke="#98A2B3" strokeWidth={1.5}
                                          markerEnd="url(#approvalArrow)"/>
                                ) : null}
                            </g>
                        );
                    })}
                </svg>
            </div>

            <Typography style={{fontSize: 11.5, color: Grey6, marginTop: 2}}>
                Each stage must be satisfied before the next opens. One rejection ends the request.
            </Typography>
        </div>
    );
}
